import React, { useState, useEffect, useRef } from "react";

import config from "../../../util/config";

import { useParams } from "react-router-dom";

import type { SVGProps } from "react";
import ApplicationForms from "../../../components/college-details/sidebar/ApplicationForms";
import CourseQuizBanner from "../../../components/exam-details/CourseQuizBanner";
import TabContent from "./TabContent";
import CoursesList from "../../../components/college-details/sidebar/CoursesList";

export function EpRight(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 1024 1024"
      {...props}
    >
      <path
        fill="currentColor"
        d="M754.752 480H160a32 32 0 1 0 0 64h594.752L521.344 777.344a32 32 0 0 0 45.312 45.312l288-288a32 32 0 0 0 0-45.312l-288-288a32 32 0 1 0-45.312 45.312z"
      ></path>
    </svg>
  );
}
interface Tab {
  title: string;
  name: string;
  orderIndex: number;
  subTabs?: Tab[];
}

const NavigationTabs = ({
  tabs,
  collegeId,
  collegeTitle,
}: {
  tabs: Tab[];
  collegeId: number;
  collegeTitle: string;
}) => {
  const { collegeName } = useParams<{ collegeName: string }>();
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.name || "");
  const [isSticky, setIsSticky] = useState(false);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateTabFromHash = () => {
      const hashTab = window.location.hash.replace("#", "");
      if (hashTab && tabs.some((tab) => tab.name === hashTab)) {
        setActiveTab(hashTab);
      } else if (tabs.length > 0) {
        const defaultTab =
          tabs.find((tab) => tab.name === "overview") || tabs[0];
        setActiveTab(defaultTab.name);
      }
    };
    updateTabFromHash();
    window.addEventListener("hashchange", updateTabFromHash);
    return () => {
      window.removeEventListener("hashchange", updateTabFromHash);
    };
  }, [tabs]);

  const checkScrollable = () => {
    const tabContainer = tabContainerRef.current;
    if (tabContainer) {
      setCanScrollLeft(tabContainer.scrollLeft > 0);
      setCanScrollRight(
        tabContainer.scrollWidth >
          tabContainer.scrollLeft + tabContainer.clientWidth
      );
    }
  };
  const handleScroll = () => {
    setIsSticky(window.scrollY >= 730);
  };
  useEffect(() => {
    checkScrollable();
    window.addEventListener("resize", checkScrollable);
    tabContainerRef.current?.addEventListener("scroll", checkScrollable);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", checkScrollable);
      tabContainerRef.current?.removeEventListener("scroll", checkScrollable);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollByAmount = (direction: "left" | "right") => {
    const tabContainer = tabContainerRef.current;
    if (tabContainer) {
      const scrollAmount = tabContainer.clientWidth / 2;
      tabContainer.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleTabChange = (tabName: string) => {
    if (tabName !== activeTab) {
      setActiveTab(tabName);
      window.location.hash = `#${tabName}`;

      tabsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div>
      <div
        className={`transition-opacity duration-500 ${
          isSticky
            ? "fixed top-0 left-0 w-full opacity-100 z-50 shadow-lg"
            : "opacity-100"
        }`}
      >
        <ul
          className="flex items-center justify-start space-x-4 px-4 py-2 overflow-x-auto"
          style={{
            background: "linear-gradient(180deg, #173CBA 0%, #06AFA9 100%)",
          }}
        >
          <button
            className={`bg-[#00CC99] border border-white rounded text-white p-2 hidden sm:block ${
              canScrollLeft ? "opacity-100" : "opacity-50 cursor-not-allowed"
            }`}
            onClick={() => canScrollLeft && handleScrollByAmount("left")}
          >
            <span className="material-icons">
              <EpRight className="rotate-180" />
            </span>
          </button>

          <div
            id="tabContainer"
            ref={tabContainerRef}
            className="overflow-x-scroll sm:overflow-x-auto w-full mx-auto mobile-scroll "
          >
            <ul className="list-none flex gap-4 items-center text-center w-full mx-auto xl:justify-start">
              {tabs.map((tab) => (
                <li
                  key={tab.orderIndex}
                  className={`text-base py-1.5 px-3.5 font-semibold rounded-sm cursor-pointer whitespace-nowrap ${
                    activeTab === tab.name
                      ? "bg-green text-white"
                      : "bg-white text-black hover:bg-green hover:text-white"
                  }`}
                  onClick={() => handleTabChange(tab.name)}
                >
                  {tab.title}
                </li>
              ))}
            </ul>
          </div>

          <button
            className={`bg-[#00CC99] border border-white rounded text-white p-2 hidden sm:block ${
              canScrollRight ? "opacity-100" : "opacity-50 cursor-not-allowed"
            }`}
            onClick={() => canScrollRight && handleScrollByAmount("right")}
          >
            <span className="material-icons">
              <EpRight />
            </span>
          </button>
        </ul>
      </div>
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row gap-6 lg:gap-9 py-6 md:py-8 px-2 sm:px-0">
          {/* Left Content */}
          <div className="w-full md:w-8/12 lg:w-9/12 min-h-[400px] md:min-h-[500px]">
            <TabContent
              collegeName={collegeName || collegeTitle}
              tabName={activeTab}
            />
          </div>

          {/* Right Sidebar */}
          <div className="w-full md:w-4/12 lg:w-3/12 hidden md:block">
            <div className="flex flex-col gap-20 bg-[#DFE0E0] px-4 py-8 sticky top-20">
              <ApplicationForms />
              <CoursesList />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationTabs;
